import { useQuery } from "@tanstack/react-query";
import InstallBar from "../components/InstallBar";
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ApiError,
  can,
  exportChapter,
  getBook,
  getChapter,
  getManifest,
  isPlusRequired,
  listChapterImages,
} from "../api";
import PlusOffer from "../components/PlusOffer";
import Placeholder, { Pending, TextLines } from "../components/Placeholder";
import ReaderDrawer from "../components/ReaderDrawer";
import { MenuIcon } from "../components/icons";
import { useT } from "../i18n";
import ChapterNav from "../player/ChapterNav";
import ChapterPlayer from "../player/ChapterPlayer";
import { atChapterEnd } from "../player/autoPlay";
import { neighbours, useReaderChapters } from "../player/chapters";
import ReadingText from "../player/ReadingText";
import { useReaderSettings } from "../player/readerSettings";
import { useReaderSpeakers } from "../player/speakers";
import { useReadingClock } from "../player/readingClock";
import { SharedAudio } from "../player/sharedAudio";
import { useAutoPlayCountdown } from "../player/useAutoPlay";
import { primaryOrg, useSession } from "../session";

/**
 * ReadPage is the reader's view of a chapter: the text, followed as it is
 * spoken, with the transport pinned under the thumb.
 *
 * Deliberately has nothing that makes a book. No estimate, no Narrate, no
 * corrections, no fix list — those spend the owner's money and change what
 * every other listener hears, and they belong to whoever is making the book.
 * A creator reaches the same chapter through the studio, which has all of it.
 *
 * The two views share the player. What differs is what surrounds it.
 *
 * Nothing is pinned above the text. The app header and a chapter bar under it
 * took a sixth of a phone screen for the whole length of a chapter, to show a
 * title the reader already knew. The way out, the rest of the app and the
 * reading settings are one floating button away, in a drawer from the starting
 * edge; the title is the top of the page and scrolls away with it.
 */
export default function ReadPage() {
  const { chapterID = "" } = useParams();
  // One chapter's page per chapter. Moving to the next one from its end is a
  // new page — at its top, with none of the last one's player or chat state.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [chapterID]);
  // The one <audio> element, kept across chapters (sharedAudio.ts): auto-play
  // starts the next chapter on the element the listener already started.
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  return (
    <SharedAudio.Provider value={audio}>
      <audio ref={setAudio} preload="metadata" data-testid="audio" />
      {audio && <ChapterReader key={chapterID} chapterID={chapterID} />}
    </SharedAudio.Provider>
  );
}

function ChapterReader({ chapterID }: { chapterID: string }) {
  const { me, isSignedIn } = useSession();
  const audio = useContext(SharedAudio);
  const org = primaryOrg(me);
  const t = useT();
  const [menuOpen, setMenuOpen] = useState(false);
  // Applies the reader's text size and theme while a chapter is open. They are
  // chosen on /settings.
  const [settings] = useReaderSettings();
  // Who is speaking (requirement 0058): a face beside each named line and a
  // play's heads set as heads (ADR-0186), from the same response the app reads.
  const speakers = useReaderSpeakers(chapterID, settings.speakers);

  // What readers are served: the published edition (ADR-0024).
  const chapter = useQuery({
    queryKey: ["chapter", chapterID, "reader"],
    queryFn: () => getChapter(chapterID),
  });
  const manifest = useQuery({
    queryKey: ["manifest", chapterID, "reader"],
    queryFn: () => getManifest(chapterID),
    // A 404 means "not narrated yet", which is a state, not a failure.
    retry: false,
    // The URLs inside expire; never serve a cached manifest with dead links.
    gcTime: 0,
    staleTime: 0,
  });

  // The chapter's pictures as the published edition has them (ADR-0038). A
  // chapter with none is the ordinary case, so a failure here is quiet: the
  // text is the thing, and nothing about it depends on the pictures arriving.
  const images = useQuery({
    queryKey: ["chapter-images", chapterID, "reader"],
    queryFn: () => listChapterImages(chapterID),
    retry: false,
  });

  // The book, for its language: its titles end no sentence of the text
  // ("Mr. Golyadkin"). The book page's own key, so arriving from it is free;
  // until it lands the text splits with no titles.
  const bookID = chapter.data?.book_id;
  // A signed-in reader's time on this page, read and listened apart (ADR-0190 §6).
  useReadingClock(bookID, audio, isSignedIn);
  const book = useQuery({
    queryKey: ["book", bookID],
    queryFn: () => getBook(bookID!),
    enabled: !!bookID,
  });

  // The book's chapters, for the drawer and the way on from the end. Quiet on
  // failure: reading this chapter does not depend on them.
  const chapters = useReaderChapters(chapter.data?.book_id);
  const chapterList = chapters.data?.items ?? [];

  // Auto-play next chapter (ADR-0177): the rule decides at the chapter's end;
  // going on is the Next button's navigation, asking the next page to play.
  const navigate = useNavigate();
  const next = neighbours(chapterList, chapterID).next;
  const goNow = useCallback(() => {
    if (next) navigate(`/read/${next.id}`, { state: { autoplay: true } });
  }, [navigate, next]);
  const countdown = useAutoPlayCountdown(goNow);
  const onEnd = () => {
    const step = atChapterEnd({
      setting: settings.autoplay,
      mode: settings.mode,
      next,
      inFront: !document.hidden,
    });
    if (step === "countdown") countdown.start();
  };
  const { event: countdownEvent } = countdown;
  useEffect(() => {
    if (!settings.autoplay) countdownEvent("setting_off");
  }, [settings.autoplay, countdownEvent]);

  const narrated = !!manifest.data;
  // A 404 means "not narrated yet", which is a state and not a failure — but
  // it is also not something the reader is told. The page simply has no player.
  const stillLoadingAudio =
    manifest.isPending ||
    (manifest.isError && !(manifest.error instanceof ApiError && manifest.error.status === 404));

  // Nothing below the title can be drawn until both the chapter and the
  // manifest have spoken: the manifest decides whether the reader gets the
  // player or the plain text, so drawing either before it lands is a guess
  // that gets replaced.
  const waiting = chapter.isPending || stillLoadingAudio;

  // Whoever can edit this book gets a way back to the tools. Everyone else
  // never learns they exist.
  const editable = !!(can(org, "chapter.write") && chapter.data && org);

  // A visitor keeps the chat and is refused by the server, in the thread
  // (ADR-0070). Hiding it was the old shape: the feature is there, and the
  // answer to "why can't I?" belongs where the question was asked rather than
  // in the absence of a control. Still needs a book: a conversation belongs
  // to one.
  const allowAsking = !!chapter.data;

  // A Plus chapter opened by link by a reader without Plus (ADR-0193): the
  // offer, and back to this chapter once they have it.
  if (isPlusRequired(chapter.error)) {
    return (
      <main className="mx-auto flex max-w-md justify-center px-4 pt-10">
        <PlusOffer then={`/read/${chapterID}`} />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-3xl px-4 pb-8">
      <button
        type="button"
        data-testid="reader-menu"
        aria-label={t.drawer.menu}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen(true)}
        /* The starting corner, clear of the notch: top left in English, top
           right in Persian. Surface-tinted and blurred so it stays legible over
           whatever line scrolls under it. */
        className="fixed start-3 top-[max(0.75rem,env(safe-area-inset-top))] z-30 grid size-10 place-items-center rounded-full border border-line bg-surface/85 text-ink-soft backdrop-blur-md transition-colors hover:text-ink"
      >
        <MenuIcon />
      </button>

      {/* On a phone opened from a link: the app, beside the menu button and
          above the title — in the flow, never over the text (ADR-0209). */}
      <InstallBar className="ms-12 mt-[max(0.75rem,env(safe-area-inset-top))]" />

      {/* Indented past the menu button on the side it sits, so a title never
          runs underneath it. No recording badge here: how the audio was made
          is the author's question, asked in the studio, not the reader's. */}
      <div className="mb-5 flex min-h-16 items-center gap-2 ps-12 pt-[max(0.75rem,env(safe-area-inset-top))]">
        {chapter.isPending ? (
          <Placeholder className="h-6 w-3/5 rounded-full" />
        ) : (
          <h1
            dir="auto"
            data-testid="chapter-title"
            className="min-w-0 flex-1 text-xl font-semibold tracking-tight"
          >
            {chapter.data?.title ?? "…"}
          </h1>
        )}
      </div>

      {/* The chapter's own shape while it is being fetched (ADR-0068). The
          title and the text are reserved; the transport is not, because
          whether this chapter has audio is exactly what the manifest has not
          said yet, and a player footprint that then vanishes moves the page
          under a thumb already reaching for it. */}
      {waiting && (
        <Pending label={t.common.loadingChapter} testID="chapter-placeholder">
          <TextLines lines={10} />
        </Pending>
      )}

      {narrated && (
        <ChapterPlayer
          chapterID={chapterID}
          chapterNumber={chapterList.findIndex((c) => c.id === chapterID) + 1}
          bookID={chapter.data?.book_id}
          title={chapter.data?.title}
          manifest={manifest.data!}
          images={images.data?.items}
          allowAsking={allowAsking}
          onEnd={onEnd}
          onListenerMove={countdownEvent}
          speakers={speakers}
          footer={
            <>
              <ChapterNav
                chapters={chapterList}
                chapterID={chapterID}
                countdown={countdown.running ? countdown : null}
                onGoNow={() => countdownEvent("next")}
              />
              <Download chapterID={chapterID} />
            </>
          }
        />
      )}

      {/* The text is still readable when there is no audio — a book you cannot
          listen to yet is still a book, and it can still answer questions.
          Held back until the manifest has spoken, or the chapter would paint
          as unnarrated for a moment and then swap under the reader. */}
      {!narrated && !stillLoadingAudio && chapter.data?.body && (
        <ReadingText
          chapterID={chapterID}
          chapterNumber={chapterList.findIndex((c) => c.id === chapterID) + 1}
          bookID={chapter.data.book_id}
          language={book.data?.language ?? ""}
          body={chapter.data.body}
          emphasis={chapter.data.emphasis}
          images={images.data?.items}
          allowAsking={allowAsking}
          trackProgress
          speakers={speakers}
          footer={<ChapterNav chapters={chapterList} chapterID={chapterID} />}
        />
      )}

      {menuOpen && (
        <ReaderDrawer
          me={me}
          bookID={chapter.data?.book_id}
          chapterID={chapterID}
          chapters={chapterList}
          studioChapterID={editable ? chapterID : undefined}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </main>
  );
}

/**
 * Download sits below the transcript rather than in the transport: it is a
 * once-per-chapter action, and the bar under the thumb is for the things done
 * repeatedly.
 */
function Download({ chapterID }: { chapterID: string }) {
  const t = useT();
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Stops the poll when the reader leaves the page.
   *
   * It is a plain async loop rather than a query, and an async loop with no way
   * out keeps calling the API for its whole length after the component is gone
   * — a minute of requests for a chapter nobody is looking at any more.
   */
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  /**
   * Polls until the file exists.
   *
   * Backing off rather than every two seconds: a short chapter is ready on the
   * second ask and a long one takes minutes, and a fixed interval has to
   * choose which of those to serve badly. This covers about four minutes in
   * twenty asks instead of one minute in thirty.
   *
   * Giving up is not a failure and does not say it is. The job is still
   * running, its output is content-addressed, and pressing the button again
   * picks up whatever has been built — so the message says to come back rather
   * than that something went wrong.
   */
  async function prepare() {
    setBusy(true);
    setError(null);
    try {
      let wait = 1000;
      for (let attempt = 0; attempt < 20 && live.current; attempt++) {
        const result = await exportChapter(chapterID);
        if (!live.current) return;
        if (result.status === "ready" && result.url) {
          setUrl(result.url);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, wait));
        wait = Math.min(Math.round(wait * 1.4), 20_000);
      }
      if (live.current) setError(t.reader.downloadSlow);
    } catch {
      if (live.current) setError(t.reader.downloadFailed);
    } finally {
      if (live.current) setBusy(false);
    }
  }

  return (
    <section className="mt-8 border-t border-line pt-5">
      {url ? (
        <a
          href={url}
          download
          data-testid="download-ready"
          className="rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk"
        >
          {t.reader.saveMp3}
        </a>
      ) : (
        <button
          type="button"
          data-testid="download-chapter"
          onClick={() => void prepare()}
          disabled={busy}
          className="rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
        >
          {busy ? t.reader.preparing : t.reader.download}
        </button>
      )}
      {error && (
        <p
          role="alert"
          data-testid="download-error"
          className="mt-2 text-sm text-red-600 dark:text-red-400"
        >
          {error}
        </p>
      )}
    </section>
  );
}
